import React, { FC, useEffect, useState } from 'react';
import ZoomVideo from '@zoom/videosdk';
import PageWrap from 'components/PageWrap';
import ZoomContext from 'contexts/ZoomContext';
import ZoomVideoOutlet from './ZoomVideoOutlet';
import { toast } from 'components/Toast';

import { CONF_ZOOM_KEY, CONF_ZOOM_SECRET } from 'utils/config';
import { fullName, logZoomErrorToSentry } from 'utils/helpers';
import { b64DecodeUnicode, generateVideoToken } from 'utils/zoomHelpers';
import { IConsultationDetails } from 'utils/models';
import { useParams } from 'react-router-dom';

import { useConsultationDetails } from 'hooks/useConsultationDetails';

const zmClient = ZoomVideo.createClient();

const ZoomVideoLayout: FC = () => {
  const { id } = useParams<{ id: string }>();

  const [isError, setIsError] = useState(false);
  const [meetingArgs, setMeetingArgs] = useState<any>(null);

  const { data, isLoading } = useConsultationDetails(id);
  const consultationDetails: IConsultationDetails | undefined = data?.consultation;

  useEffect(() => {
    if (data && data.success === false) {
      logZoomErrorToSentry(new Error(data.message || 'Unauthorized consultation access'), {
        component: 'ZoomVideoLayout',
        action: 'fetchConsultationDetails',
        consultationId: id,
        errorMessage: data.message
      });
      toast({
        title: 'Error',
        message: data.message || 'You are not authorized to make this consultation',
        variant: 'error'
      });
      setIsError(true);
    }
  }, [data, id]);

  useEffect(() => {
    const script = document.createElement('script');
    script.async = true;
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    if (!consultationDetails || !id) return;

    const baseArgs = Object.fromEntries(new URLSearchParams(window.location.search));
    const decodedArgs: Record<string, any> = { ...baseArgs };
    if (baseArgs.web && baseArgs.web !== '0') {
      ['topic', 'name', 'password', 'sessionKey', 'userIdentity'].forEach((field) => {
        if (Object.hasOwn(baseArgs, field)) {
          try {
            decodedArgs[field] = b64DecodeUnicode(baseArgs[field]);
          } catch (error) {
            logZoomErrorToSentry(error as Error, {
              component: 'ZoomVideoLayout',
              action: 'decodeBase64Field',
              consultationId: id,
              referenceId: consultationDetails?.reference_id,
              field,
              fieldValue: baseArgs[field]
            });
          }
        }
      });
    }

    const role = parseInt(decodedArgs.role || '1', 10);
    [
      'enforceGalleryView',
      'enforceVB',
      'cloud_recording_option',
      'cloud_recording_election'
    ].forEach((field) => {
      if (decodedArgs[field] !== undefined) {
        // @ts-ignore
        decodedArgs[field] = Number(decodedArgs[field]) || 0;
      }
    });

    const finalArgs = {
      sdkKey: CONF_ZOOM_KEY,
      sdkSecret: CONF_ZOOM_SECRET,
      webEndpoint: 'zoom.us',
      topic: `session-${consultationDetails.reference_id}-${id}`,
      name: `Dr. ${fullName(consultationDetails.doctor || null)}`,
      password: '',
      sessionKey: decodedArgs.sessionKey || '',
      userIdentity: decodedArgs.userIdentity || '',
      role,
      enforceGalleryView: decodedArgs.enforceGalleryView || 0,
      enforceVB: decodedArgs.enforceVB || 0,
      cloud_recording_option: decodedArgs.cloud_recording_option || '',
      cloud_recording_election: decodedArgs.cloud_recording_election || '',
      telemetry_tracking_id: decodedArgs.telemetry_tracking_id
        ? b64DecodeUnicode(decodedArgs.telemetry_tracking_id)
        : '',
      web: '1'
    };

    console.log('Final Zoom Video Args: ', finalArgs);

    // @ts-ignore signature generation unchanged
    finalArgs.signature = generateVideoToken(
      finalArgs.sdkKey,
      finalArgs.sdkSecret,
      finalArgs.topic,
      finalArgs.sessionKey,
      finalArgs.userIdentity,
      finalArgs.role,
      finalArgs.cloud_recording_option,
      finalArgs.cloud_recording_election,
      finalArgs.telemetry_tracking_id
    );

    setMeetingArgs(finalArgs);
  }, [consultationDetails, id]);

  if (!meetingArgs || isLoading) {
    return (
      <PageWrap title="On going Consultation">
        <div className=" h-screen flex items-center justify-center text-gray-600">
          Preparing session...
        </div>
      </PageWrap>
    );
  }

  return (
    <PageWrap title="On going Consultation">
      <main className="flex flex-col h-screen bg-hazel-bone-100 relative">
        <ZoomContext.Provider value={zmClient}>
          <ZoomVideoOutlet
            meetingArgs={meetingArgs}
            consultationDetails={consultationDetails}
            isError={isError}
          />
        </ZoomContext.Provider>
      </main>
    </PageWrap>
  );
};

export default ZoomVideoLayout;
